import { RadialSeparator } from '../RadialSeparator';
import { RadialSeparatorsProps } from '../../types';
import { range } from 'lodash';
import { useMemo } from 'react';
import { getRoleplayVisibleSeparatorIndices } from '../../config';

/**
 * Renders the full set of radial separator ticks around the score circle,
 * based on the provided values and variant.
 *  - In 'pass/fail' mode, separators are conditionally hidden based on the current value and index.
 *  - In 'role-play' mode, only the separators at indices 20, 50, and 90 are rendered.
 */
export const RadialSeparators: React.FC<RadialSeparatorsProps> = ({
  circleRatio,
  indexMax,
  numSeparators,
  rotation,
  runtimeRatingCutoffs,
  value,
  variant,
}) => {
  const visibleSeparatorIndices = useMemo(
    () => getRoleplayVisibleSeparatorIndices(runtimeRatingCutoffs),
    [runtimeRatingCutoffs],
  );
  /**
   * The `values` array contains the turn values for each separator, calculated
   * as: `(circleRatio / numSeparators) * index + rotation`. This ensures that
   * the separators are evenly spaced around the circle according to the
   * specified ratio and rotation.
   */
  const values = useMemo(() => {
    return range(numSeparators + 1).map((index) => {
      return (circleRatio / numSeparators) * index + rotation;
    });
  }, [circleRatio, numSeparators, rotation]);

  /**
   * The `style` object defines the visual styling for the separator ticks, with
   * different colors for 'role-play' and 'pass/fail' modes. The height and width
   * are set to create thin vertical ticks. This style is memoized to avoid
   * unnecessary recalculations on re-renders, and it only changes when the
   * `variant` prop changes.
   */
  const style: React.CSSProperties = useMemo(() => {
    const rolePlayColor = 'var(--color-neutral-white)';
    const passFailColor = 'var(--color-neutral-gray-400)';
    const background = variant === 'role-play' ? rolePlayColor : passFailColor;

    return {
      background,
      height: '12.381px',
      width: '2px',
    };
  }, [variant]);

  // =================================================
  // Render radial separators based on the current
  // variant and value.
  // =================================================
  return values.map((turns, index: number) => {
    // =================================================
    // Pass/Fail mode: Hide separators outside the visible
    // arc and fade in/out around the animated boundary.
    // =================================================
    if (variant === 'pass/fail') {
      // Keep separators outside the visible pass/fail arc hidden.
      if (index >= indexMax) {
        return null;
      }

      // Fade separators in/out around the animated boundary as the score changes.
      // Start one tick after the current score so the first gray separator does
      // not visually flatten a rounded path endpoint.
      const opacity = Math.max(0, Math.min(1, index - value - 1));

      // Render
      return <RadialSeparator key={turns} opacity={opacity} style={style} turns={turns} />;
    }

    // =================================================
    // Role-play mode: Only render separators at specific
    // indices to create distinct score bands.
    // =================================================
    if (!visibleSeparatorIndices.has(index)) {
      return null;
    }

    // Render
    return <RadialSeparator key={turns} style={style} turns={turns} />;
  });
};

RadialSeparators.displayName = 'RadialSeparators';

export default RadialSeparators;
